'use client';

/**
 * UserPreferencesProvider
 *
 * Loads the current user's preferences once on mount (via React Query) and
 * makes them available throughout the tree via context. Falls back to defaults
 * immediately so consumers never need to null-check.
 *
 * Also syncs the user's saved theme to localStorage so next-themes picks it up
 * on next load (persists theme across devices/browsers).
 *
 * Usage:
 *   const { countryCode } = useUserPreferences();
 */

import { createContext, useContext, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from 'next-themes';
import { fetchCurrentUserPreferences, type UserPreferences } from '@/lib/api';

const DEFAULT_PREFERENCES: UserPreferences = {
  countryCode: 'US',
  theme: 'system',
  defaultCollectionVisibility: 'public',
  emailNotifications: {
    circleDigest: true,
    collaboratorInvites: true,
    newFollowers: true,
  },
};

const UserPreferencesContext = createContext<UserPreferences>(DEFAULT_PREFERENCES);

export function UserPreferencesProvider({ children }: { children: React.ReactNode }) {
  const { setTheme } = useTheme();

  const { data } = useQuery({
    queryKey: ['user', 'preferences'],
    queryFn: async () => {
      const res = await fetchCurrentUserPreferences();
      return res.user?.preferences ?? null;
    },
    staleTime: 5 * 60 * 1000, // 5 min — preferences rarely change
  });

  // Sync saved theme to next-themes on load
  useEffect(() => {
    if (data?.theme) {
      setTheme(data.theme);
    }
  }, [data?.theme, setTheme]);

  const preferences: UserPreferences = data
    ? {
        ...DEFAULT_PREFERENCES,
        ...data,
        emailNotifications: {
          ...DEFAULT_PREFERENCES.emailNotifications,
          ...(data.emailNotifications ?? {}),
        },
      }
    : DEFAULT_PREFERENCES;

  return (
    <UserPreferencesContext.Provider value={preferences}>
      {children}
    </UserPreferencesContext.Provider>
  );
}

export function useUserPreferences(): UserPreferences {
  return useContext(UserPreferencesContext);
}
